/**
 * Utility functions for AWS Integration Form
 */

export type RegionOption = {
  label: string;
  value: string;
  isChecked: boolean;
};

/**
 * Helper function to get default time (12:00 AM) for Add mode
 */
export const getDefaultTime = (): Date => {
  const defaultTime = new Date();
  defaultTime.setHours(0, 0, 0, 0);
  return defaultTime;
};

/**
 * Validate if at least one region is selected
 */
export const areRegionsValid = (
  selectedRegions: Array<RegionOption>,
): boolean => {
  return selectedRegions.length > 0;
};

/**
 * Check if form is valid (all required fields are valid)
 * Integrate button is only enabled when Role ARN and External ID are visible (after CFT download)
 * and all fields have valid values
 */
export const isFormValid = (
  formData: Record<string, unknown>,
  cftDownloaded: boolean,
  selectedRegions: Array<RegionOption>,
  importTime: Date | null,
  frequency: string = "24hours",
): boolean => {
  // Button is always disabled until CFT is downloaded (Role ARN and External ID are not visible yet)
  if (!cftDownloaded) {
    return false;
  }

  // After CFT download, all fields must be valid:
  // Name validation: must not be empty
  const nameValid = Boolean(
    formData.name && String(formData.name).trim().length > 0,
  );

  // Account ID validation: must be 12 digits
  const accountIdValid = /^\d{12}$/.test(String(formData.account_id || ""));

  // Role ARN validation: must be valid ARN format (required when visible)
  const roleArnValid = /^arn:aws:iam::\d{12}:role\/[a-zA-Z0-9+=,.@_-]+$/.test(
    String(formData.role_arn || ""),
  );

  // External ID validation: must not be empty (required when visible)
  const externalIdValid = Boolean(
    formData.external_id && String(formData.external_id).trim().length > 0,
  );

  // Regions validation: at least one region must be selected (required)
  const regionsValid = areRegionsValid(selectedRegions);

  // Import time validation: only required when frequency is daily (24hours)
  const importTimeValid = frequency !== "24hours" || importTime !== null;

  return (
    nameValid &&
    accountIdValid &&
    roleArnValid &&
    externalIdValid &&
    regionsValid &&
    importTimeValid
  );
};
